import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet } from '@/components/ui/sheet';

type MatingsPerPlate = '2' | '4';

export type SessionSettingsDrawerProps = {
  visible: boolean;
  onHide: () => void;
};

export const SessionSettingsDrawer = ({
  visible,
  onHide,
}: SessionSettingsDrawerProps) => {
  const { t } = useTranslation();

  const [matTimeWindow, setMatTimeWindow] = useState(
    () => localStorage.getItem('fertwork-mat-time-window') || '3',
  );
  const [heraCellCount, setHeraCellCount] = useState(
    () => localStorage.getItem('fertwork-heracell-count') || '0',
  );
  const [astecCount, setAstecCount] = useState(
    () => localStorage.getItem('fertwork-astec-count') || '0',
  );
  const [preference, setPreference] = useState(
    () => localStorage.getItem('fertwork-preference') || 'HeraCell',
  );
  const [matingsPerPlate, setMatingsPerPlate] = useState<MatingsPerPlate>(
    () =>
      (localStorage.getItem('fertwork-matings-per-plate') as MatingsPerPlate) ||
      '2',
  );

  const handleSave = () => {
    localStorage.setItem('fertwork-mat-time-window', matTimeWindow);
    localStorage.setItem('fertwork-heracell-count', heraCellCount);
    localStorage.setItem('fertwork-astec-count', astecCount);
    localStorage.setItem('fertwork-preference', preference);
    localStorage.setItem('fertwork-matings-per-plate', matingsPerPlate);
    onHide();
  };

  return (
    <Sheet
      open={visible}
      onOpenChange={(open) => {
        if (!open) onHide();
      }}
      title={t('fertWork.sessionSettings', 'Session Settings')}
      className="sm:max-w-96"
    >
      <div className="flex h-full flex-col gap-6">
        <div className="flex flex-col gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t('fertWork.matTimeWindow', 'Mat Time Window')}
            </label>
            <div className="flex gap-2">
              {['2', '3'].map((hours) => (
                <button
                  key={hours}
                  type="button"
                  onClick={() => setMatTimeWindow(hours)}
                  className={`flex-1 rounded border px-3 py-2 text-sm font-medium transition-colors ${
                    matTimeWindow === hours
                      ? 'border-cyan-600 bg-cyan-600 text-white'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-cyan-400'
                  }`}
                >
                  {t('fertWork.hourWindow', '{hours} Hour Window', {
                    hours: parseInt(hours),
                  })}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t('fertWork.heraCellCount', 'HeraCell Count')}
            </label>
            <Input
              type="number"
              min={0}
              value={heraCellCount}
              onChange={(e) => setHeraCellCount(e.target.value || '0')}
              className="w-full"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t('fertWork.astecCount', 'Astec Count')}
            </label>
            <Input
              type="number"
              min={0}
              value={astecCount}
              onChange={(e) => setAstecCount(e.target.value || '0')}
              className="w-full"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t('fertWork.preference', 'Preference')}
            </label>
            <div className="flex gap-2">
              {(['HeraCell', 'Astec', 'Even'] as const).map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setPreference(opt)}
                  className={`flex-1 rounded border px-3 py-2 text-sm font-medium transition-colors ${
                    preference === opt
                      ? 'border-cyan-600 bg-cyan-600 text-white'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-cyan-400'
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              {t('fertWork.matingsPerPlate', 'Matings Per Plate')}
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setMatingsPerPlate('2')}
                className={`flex-1 rounded border px-3 py-2 text-sm font-medium transition-colors ${
                  matingsPerPlate === '2'
                    ? 'border-cyan-600 bg-cyan-600 text-white'
                    : 'border-gray-300 bg-white text-gray-600 hover:border-cyan-400'
                }`}
              >
                {t('fertWork.standard2Matings', 'Standard (2)')}
              </button>
              <button
                type="button"
                onClick={() => setMatingsPerPlate('4')}
                className={`flex-1 rounded border px-3 py-2 text-sm font-medium transition-colors ${
                  matingsPerPlate === '4'
                    ? 'border-cyan-600 bg-cyan-600 text-white'
                    : 'border-gray-300 bg-white text-gray-600 hover:border-cyan-400'
                }`}
              >
                {t('fertWork.fourMatingsPerPlate', '4 Per Plate')}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {t(
                'fertWork.fourMatingsDescription',
                'Use 4 matings per plate with a 4×2 incubator grid layout.',
              )}
            </p>
          </div>
        </div>

        <div className="mt-auto border-t pt-4">
          <Button className="w-full" onClick={handleSave}>
            <i className="pi pi-check" data-icon="inline-start" />
            {t('common.save', 'Save')}
          </Button>
        </div>
      </div>
    </Sheet>
  );
};
